import { useState, type ReactNode } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { downloadDocument, downloadNameFor, type IDocumentVersion } from '@apis/documents';
import { FilePreview } from '../FilePreview/FilePreview';
import { SingleSelect } from '@components/ui/SingleSelect/SingleSelect';
import { formatFileSize } from '@/utils/formatFileSize';
import { formatProjectDateTime } from '@/utils/formatProjectDate';
import { DownloadIcon, LoaderIcon } from '@/utils/icons';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './CompareModal.module.css';

interface ICompareModalProps {
  open: boolean;
  siteId: string;
  fileName: string;
  approvedVersion: number | null;
  versions: IDocumentVersion[];
  /** The right-hand version (URL-owned; the latest is always the left). */
  selectedVersion: number;
  onSelectVersion: (version: number) => void;
  onClose: () => void;
}

// Side-by-side comparison of two versions: the current (latest) one is pinned on
// the left, and the right side is a picker over the remaining versions. Versions
// arrive newest-first, so [0] is current and [1..] are the comparable history.
export function CompareModal({ open, siteId, fileName, approvedVersion, versions, selectedVersion, onSelectVersion, onClose }: ICompareModalProps) {
  /* Document stamps render in the PROJECT's zone, never the viewer's.
     Undefined only during the bootstrap window before the working site
     resolves — an em-dash then, rather than a date in a guessed zone. */
  const tz = useSelector(selectSiteTz);
  const { t } = useTranslation();
  // The physical id currently downloading, so one busy state guards re-clicks
  // across both cards.
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState(false);

  const latest = versions[0];
  const others = versions.slice(1);

  // Selection is controlled (it lives in the URL); a stale version number
  // falls back to the newest comparable one.
  const selected = others.find((v) => v.version === selectedVersion) ?? others[0];

  const options = others.map((v) => ({
    id: v.fileId,
    label: t('documents.versionShort', { version: v.version }),
  }));

  async function handleDownload(version: IDocumentVersion) {
    if (downloadingId) return;
    setDownloadingId(version.fileId);
    setDownloadError(false);
    try {
      await downloadDocument(siteId, version.fileId, downloadNameFor(fileName, version.version, approvedVersion));
    } catch (err) {
      console.error('document download failed', err);
      setDownloadError(true);
    } finally {
      setDownloadingId(null);
    }
  }

  function renderCard(version: IDocumentVersion, header: ReactNode) {
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>{header}</div>
        <FilePreview siteId={siteId} doc={{ id: version.fileId, name: fileName, sizeBytes: version.sizeBytes }} />
        <div className={styles.cardMeta}>
          <span className={styles.metaItem}>{version.author}</span>
          <span className={styles.metaItem}>{tz ? formatProjectDateTime(version.date, tz) : '—'}</span>
          <span className={styles.metaItem}>{formatFileSize(version.sizeBytes)}</span>
        </div>
        <button
          type="button"
          className={styles.downloadBtn}
          onClick={() => handleDownload(version)}
          disabled={downloadingId !== null}
          aria-busy={downloadingId === version.fileId}
          aria-label={t('documents.downloadVersion', { version: version.version })}
        >
          {downloadingId === version.fileId ? (
            <>
              <span className={styles.downloadSpinner}>
                <LoaderIcon />
              </span>
              {t('documents.downloading')}
            </>
          ) : (
            <>
              <DownloadIcon />
              {t('documents.download')}
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.backdrop} />
        <Dialog.Popup className={styles.panel}>
          <Dialog.Title className={styles.title}>
            {t('documents.compareTitle', { name: fileName })}
          </Dialog.Title>

          <ScrollArea orientation="horizontal" viewportClassName={styles.grid}>
            {latest && renderCard(latest, (
              <>
                <span className={styles.versionNum}>{t('documents.versionShort', { version: latest.version })}</span>
                <span className={styles.latestBadge}>{t('documents.latest')}</span>
              </>
            ))}
            {selected && renderCard(selected, (
              // With a single comparable version there's nothing to choose so label is static
              others.length > 1 ? (
                <SingleSelect
                  value={selected?.fileId ?? ''}
                  onChange={(fileId) => {
                    const version = others.find((v) => v.fileId === fileId);
                    if (version) onSelectVersion(version.version);
                  }}
                  options={options}
                  placeholder={t('documents.compareSelectPlaceholder')}
                  ariaLabel={t('documents.compareSelectAria')}
                  elevated
                />
              ) : (
                <span className={styles.versionNum}>{t('documents.versionShort', { version: selected.version })}</span>
              )
            ))}
          </ScrollArea>

          {downloadError && <p className={styles.error} role="alert">{t('documents.downloadError')}</p>}

          <div className={styles.actions}>
            <Dialog.Close className={styles.closeBtn}>
              {t('documents.close')}
            </Dialog.Close>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
